import type { Achievement, AchievementTier, EarnedAchievement } from '@foosball/shared';

const TIER_STYLES: Record<AchievementTier, string> = {
  bronze: 'border-amber-700/40 bg-amber-50 dark:border-amber-700/50 dark:bg-amber-950/40',
  silver: 'border-slate-400/50 bg-slate-50 dark:border-slate-500/50 dark:bg-slate-800/60',
  gold: 'border-yellow-500/50 bg-yellow-50 dark:border-yellow-600/50 dark:bg-yellow-950/40',
};

function Badge({
  achievement,
  earned,
}: {
  achievement: Achievement;
  earned: boolean;
}) {
  return (
    <li
      title={achievement.description}
      className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center ${
        earned ? TIER_STYLES[achievement.tier] : 'border-dashed border-slate-200 dark:border-slate-800'
      }`}
    >
      {/*
        Locked badges keep their icon but are desaturated and dimmed rather than
        hidden — seeing what is still available is half the point of a shelf.
      */}
      <span className={`text-2xl ${earned ? '' : 'opacity-25 grayscale'}`} aria-hidden="true">
        {achievement.icon}
      </span>
      <span
        className={`text-[11px] font-medium leading-tight ${
          earned ? '' : 'text-slate-400 dark:text-slate-600'
        }`}
      >
        {achievement.name}
      </span>
      <span className="sr-only">
        {achievement.name}: {achievement.description}. {earned ? 'Earned.' : 'Not yet earned.'}
      </span>
    </li>
  );
}

export function AchievementShelf({
  catalogue,
  earned,
}: {
  catalogue: Achievement[];
  earned: EarnedAchievement[];
}) {
  const earnedCodes = new Set(earned.map((a) => a.code));

  if (catalogue.length === 0) {
    return <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No achievements yet.</p>;
  }

  return (
    <>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {earnedCodes.size} of {catalogue.length} earned
      </p>
      <ul
        aria-label="Achievements"
        className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6"
      >
        {catalogue.map((achievement) => (
          <Badge
            key={achievement.code}
            achievement={achievement}
            earned={earnedCodes.has(achievement.code)}
          />
        ))}
      </ul>
    </>
  );
}
